/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const ptPT: LocalizationResource = {
  locale: 'pt-PT',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copiar e fechar',
      formHint: 'Por razões de segurança, não permitiremos que visualize novamente mais tarde.',
      formTitle: 'Copie a sua chave API "{{name}}" agora',
    },
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: 'Voltar',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: 'Padrão',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Personificar outro dispositivo',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'Principal',
  badge__renewsAt: undefined,
  badge__requiresAction: 'Requer ação',
  badge__startsAt: undefined,
  badge__thisDevice: 'Este dispositivo',
  badge__trialEndsAt: undefined,
  badge__unverified: 'Não verificado',
  badge__upcomingPlan: undefined,
  badge__userDevice: 'Dispositivo do utilizador',
  badge__you: 'O utilizador',
  billing: {
    addPaymentMethod__label: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedMonthlyOnly: undefined,
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    keepFreeTrial: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    paymentMethods__label: undefined,
    popular: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  createOrganization: {
    formButtonSubmit: 'Criar organização',
    invitePage: {
      formButtonReset: 'Ignorar',
    },
    title: 'Criar organização',
  },
  dates: {
    lastDay: "Ontem às {{ date | timeString('pt-PT') }}",
    next6Days: "{{ date | weekday('pt-PT','long') }} às {{ date | timeString('pt-PT') }}",
    nextDay: "Amanhã às {{ date | timeString('pt-PT') }}",
    numeric: "{{ date | numeric('pt-PT') }}",
    previous6Days: "Último {{ date | weekday('pt-PT','long') }} às {{ date | timeString('pt-PT') }}",
    sameDay: "Hoje às {{ date | timeString('pt-PT') }}",
  },
  dividerText: 'ou',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'Utilize outro método',
  footerPageLink__help: 'Ajuda',
  footerPageLink__privacy: 'Privacidade',
  footerPageLink__terms: 'Termos de uso',
  formButtonPrimary: 'Continuar',
  formButtonPrimary__verify: 'Verify',
  formFieldAction__forgotPassword: 'Esqueceu a palavra-passe?',
  formFieldError__matchingPasswords: 'Passwords match.',
  formFieldError__notMatchingPasswords: "Passwords don't match.",
  formFieldError__verificationLinkExpired: 'The verification link expired. Please request a new link.',
  formFieldHintText__optional: 'Opcional',
  formFieldHintText__slug: 'A slug is a human-readable ID that must be unique. It’s often used in URLs.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: 'Insira o código de backup',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Eliminar conta',
  formFieldInputPlaceholder__emailAddress: 'Insira o seu endereço de e-mail',
  formFieldInputPlaceholder__emailAddress_username: 'Insira o seu e-mail ou nome de utilizador',
  formFieldInputPlaceholder__emailAddresses: 'Insira um ou mais endereços de e-mail separados por espaços ou vírgulas',
  formFieldInputPlaceholder__firstName: 'Insira o seu primeiro nome',
  formFieldInputPlaceholder__lastName: 'Insira o seu apelido',
  formFieldInputPlaceholder__organizationDomain: 'Insira o domínio da organização',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'Insira o endereço de e-mail do domínio da organização',
  formFieldInputPlaceholder__organizationName: 'Insira o nome da organização',
  formFieldInputPlaceholder__organizationSlug: 'Insira o identificador da organização (slug)',
  formFieldInputPlaceholder__password: 'Insira a sua palavra-passe',
  formFieldInputPlaceholder__phoneNumber: 'Insira o seu número de telefone',
  formFieldInputPlaceholder__username: 'Insira o seu nome de utilizador',
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'Chave API',
  formFieldLabel__apiKeyDescription: 'Descrição',
  formFieldLabel__apiKeyExpiration: 'Expiração',
  formFieldLabel__apiKeyName: 'Nome da chave secreta',
  formFieldLabel__automaticInvitations: 'Ativar convites automáticos para este domínio',
  formFieldLabel__backupCode: 'Código de backup',
  formFieldLabel__confirmDeletion: 'Confirmar exclusão',
  formFieldLabel__confirmPassword: 'Confirmar palavra-passe',
  formFieldLabel__currentPassword: 'Palavra-passe atual',
  formFieldLabel__emailAddress: 'Insira o seu e-mail',
  formFieldLabel__emailAddress_username: 'E-mail ou nome de utilizador',
  formFieldLabel__emailAddresses: 'Endereços de e-mail',
  formFieldLabel__firstName: 'Nome',
  formFieldLabel__lastName: 'Apelido',
  formFieldLabel__newPassword: 'Nova palavra-passe',
  formFieldLabel__organizationDomain: 'Domínio',
  formFieldLabel__organizationDomainDeletePending: 'Excluir convites e sugestões pendentes',
  formFieldLabel__organizationDomainEmailAddress: 'Endereço de e-mail de verificação',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Endereço de e-mail para receber um código e verificar este domínio',
  formFieldLabel__organizationName: 'Nome da organização',
  formFieldLabel__organizationSlug: 'URL Slug',
  formFieldLabel__passkeyName: 'Nome da Chave de Acesso',
  formFieldLabel__password: 'Palavra-passe',
  formFieldLabel__phoneNumber: 'Telemóvel',
  formFieldLabel__role: 'Função',
  formFieldLabel__signOutOfOtherSessions: 'Desconectar de todos os outros dispositivos',
  formFieldLabel__username: 'Nome de utilizador',
  impersonationFab: {
    action__signOut: 'Terminar sessão',
    title: 'Sessão iniciada como {{identifier}}',
  },
  lastAuthenticationStrategy: 'Último uso',
  maintenanceMode: 'Modo de Manutenção',
  membershipRole__admin: 'Administrador',
  membershipRole__basicMember: 'Membro',
  membershipRole__guestMember: 'Convidado',
  organizationList: {
    action__createOrganization: 'Criar organização',
    action__invitationAccept: 'Participar',
    action__suggestionsAccept: 'Solicitar participação',
    createOrganization: 'Criar organização',
    invitationAcceptedLabel: 'Participando',
    subtitle: 'para continuar no {{applicationName}}',
    suggestionsAcceptedLabel: 'Aprovação pendente',
    title: 'Selecione uma conta',
    titleWithoutPersonal: 'Selecione uma organização',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'Convites automáticos',
    badge__automaticSuggestion: 'Sugestões automáticas',
    badge__manualInvitation: 'Sem inscrição automática',
    badge__unverified: 'Não verificado',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'Adicione o domínio para verificar. Utilizadores com endereços de e-mail neste domínio podem entrar na organização automaticamente ou solicitar entrada.',
      title: 'Adicionar domínio',
    },
    invitePage: {
      detailsTitle__inviteFailed: 'Os convites não puderam ser enviados. Corrija o seguinte e tente novamente:',
      formButtonPrimary__continue: 'Enviar convites',
      selectDropdown__role: 'Select role',
      subtitle: 'Convidar novos membros para esta organização',
      successMessage: 'Convites enviados com sucesso',
      title: 'Convidar membros',
    },
    membersPage: {
      action__invite: 'Convidar',
      action__search: 'Pesquisar',
      activeMembersTab: {
        menuAction__remove: 'Remover membro',
        tableHeader__actions: 'Ações',
        tableHeader__joined: 'Entrou',
        tableHeader__role: 'Função',
        tableHeader__user: 'Utilizador',
      },
      detailsTitle__emptyRow: 'Nenhum membro para mostrar',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Convide utilizadores conectando um domínio de e-mail com a sua organização. Qualquer pessoa que se inscrever com um domínio de e-mail correspondente poderá se entrar na organização a qualquer momento.',
          headerTitle: 'Convites automáticos',
          primaryButton: 'Configurar domínios verificados',
        },
        table__emptyRow: 'Nenhum convite a mostrar',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Cancelar convite',
        tableHeader__invited: 'Convidado',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Utilizadores que se inscrevem com um domínio de e-mail correspondente podem ver uma sugestão para solicitar participação na sua organização.',
          headerTitle: 'Sugestões automáticas',
          primaryButton: 'Configurar domínios verificados',
        },
        menuAction__approve: 'Aprovar',
        menuAction__reject: 'Rejeitar',
        tableHeader__requested: 'Acesso solicitado',
        table__emptyRow: 'Nenhuma solicitação a mostrar',
      },
      start: {
        headerTitle__invitations: 'Convites',
        headerTitle__members: 'Membros',
        headerTitle__requests: 'Pedidos',
      },
      alerts: {
        roleSetMigrationInProgress: {
          title: 'As funções estão temporariamente bloqueadas',
          subtitle:
            'Estamos a atualizar as funções disponíveis. Assim que terminar, poderá atualizar as funções novamente.',
        },
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'Manage your organization.',
      general: 'General',
      members: 'Members',
      title: 'Organization',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Escreva {{organizationName}} abaixo para continuar.',
          messageLine1: 'Tem certeza de que deseja excluir esta organização?',
          messageLine2: 'Esta ação é permanente e irreversível.',
          successMessage: 'Você excluiu a organização.',
          title: 'Excluir organização',
        },
        leaveOrganization: {
          actionDescription: 'Escreva {{organizationName}} abaixo para continuar.',
          messageLine1:
            'Tem certeza de que deseja sair desta organização? Você perderá o acesso a esta organização e às suas aplicações.',
          messageLine2: 'Esta ação é permanente e não pode ser desfeita.',
          successMessage: 'Você saiu da organização.',
          title: 'Sair da organização',
        },
        title: 'Perigo',
      },
      domainSection: {
        menuAction__manage: 'Manage',
        menuAction__remove: 'Delete',
        menuAction__verify: 'Verify',
        primaryButton: 'Adicionar domínio',
        subtitle:
          'Permita que os utilizadores juntem-se à organização automaticamente ou solicitem participação com base num domínio de e-mail verificado.',
        title: 'Domínios verificados',
      },
      successMessage: 'A organização foi atualizada.',
      title: 'Perfil da organização',
    },
    removeDomainPage: {
      messageLine1: 'O domínio de e-mail {{domain}} será removido.',
      messageLine2: 'Os utilizadores não conseguirão entrar na organização após isso.',
      successMessage: '{{domain}} foi removido.',
      title: 'Excluir domínio',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: 'Membros',
      profileSection: {
        primaryButton: 'Guardar',
        title: 'Organization Profile',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'A exclusão deste domínio afetará os utilizadores convidados.',
        removeDomainActionLabel__remove: 'Excluir domínio',
        removeDomainSubtitle: 'Remova este domínio dos seus domínios verificados',
        removeDomainTitle: 'Excluir domínio',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Os utilizadores são automaticamente convidados a entrar na organização quando se inscrevem.',
        automaticInvitationOption__label: 'Convites automáticos',
        automaticSuggestionOption__description:
          'Os utilizadores recebem uma sugestão para solicitar participação, mas devem ser aprovados por um administrador antes de poderem entrar na organização.',
        automaticSuggestionOption__label: 'Sugestões automáticas',
        calloutInfoLabel: 'Alterar o modo de inscrição afetará apenas os novos utilizadores.',
        calloutInvitationCountLabel: 'Convites pendentes enviados aos utilizadores: {{count}}',
        calloutSuggestionCountLabel: 'Sugestões pendentes enviadas aos utilizadores: {{count}}',
        manualInvitationOption__description: 'Os utilizadores só podem ser convidados manualmente para a organização.',
        manualInvitationOption__label: 'Sem inscrição automática',
        subtitle: 'Escolha como os utilizadores deste domínio se podem entrar na organização.',
      },
      start: {
        headerTitle__danger: 'Perigo',
        headerTitle__enrollment: 'Opções de inscrição',
      },
      subtitle: 'O domínio {{domain}} agora está verificado. Continue por selecionar o modo de inscrição.',
      title: 'Update {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Insira o código de verificação enviado para o seu endereço de e-mail',
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'O domínio {{domainName}} precisa ser verificado por e-mail.',
      subtitleVerificationCodeScreen:
        'Um código de verificação foi enviado para {{emailAddress}}. Insira-o para continuar.',
      title: 'Verificar domínio',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'Criar organização',
    action__invitationAccept: 'Participar',
    action__manageOrganization: 'Configurar organização',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'Solicitar participação',
    notSelected: 'Nenhuma organização selecionada',
    personalWorkspace: 'Conta pessoal',
    suggestionsAcceptedLabel: 'Aprovação pendente',
  },
  paginationButton__next: 'Próximo',
  paginationButton__previous: 'Anterior',
  paginationRowText__displaying: 'Apresentando',
  paginationRowText__of: 'de',
  reverification: {
    alternativeMethods: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__backupCode: undefined,
      blockButton__emailCode: undefined,
      blockButton__passkey: undefined,
      blockButton__password: undefined,
      blockButton__phoneCode: undefined,
      blockButton__totp: undefined,
      getHelp: {
        blockButton__emailSupport: undefined,
        content: undefined,
        title: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: undefined,
      title: undefined,
    },
    emailCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    noAvailableMethods: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCodeMfa: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    totpMfa: {
      formTitle: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Add account',
      action__signOutAll: 'Sign out of all accounts',
      subtitle: 'Select the account with which you wish to continue.',
      title: 'Choose an account',
    },
    alternativeMethods: {
      actionLink: 'Ajuda',
      actionText: 'Don’t have any of these?',
      blockButton__backupCode: 'Utilize um código de backup',
      blockButton__emailCode: 'Enviar código para {{identifier}}',
      blockButton__emailLink: 'Enviar link para {{identifier}}',
      blockButton__passkey: 'Utilizar chave de acesso',
      blockButton__password: 'Fazer login com palavra-passe',
      blockButton__phoneCode: 'Enviar código para {{identifier}}',
      blockButton__totp: 'Utilize o seu autenticador',
      getHelp: {
        blockButton__emailSupport: 'E-mail de suporte',
        content:
          'Se estiver com dificuldades para entrar na sua conta, envie-nos um e-mail e iremos ajudar-te a restaurar o acesso o mais rápido possível.',
        title: 'Ajuda',
      },
      subtitle: 'Facing issues? You can use any of these methods to sign in.',
      title: 'Utilize outro método',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Insira um código de backup',
    },
    emailCode: {
      formTitle: 'Código de verificação',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    emailCodeMfa: {
      formTitle: 'Verifique o seu e-mail',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'O cliente não corresponde ao esperado. Tente novamente.',
        title: 'Erro de cliente',
      },
      expired: {
        subtitle: 'Retorne para a aba original para continuar',
        title: 'Este link de verificação expirou',
      },
      failed: {
        subtitle: 'Retorne para a aba original para continuar',
        title: 'Este link de verificação é inválido',
      },
      formSubtitle: 'Utilize o link enviado no seu e-mail',
      formTitle: 'Link de verificação',
      loading: {
        subtitle: 'Será redirecionado em breve',
        title: 'Entrando...',
      },
      resendButton: 'Não recebeu um link? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
      unusedTab: {
        title: 'Já pode fechar esta aba',
      },
      verified: {
        subtitle: 'Será redirecionado em breve',
        title: 'Login realizado com sucesso',
      },
      verifiedSwitchTab: {
        subtitle: 'Retorne para a aba original para continuar',
        subtitleNewTab: 'Retorne para a nova aba que foi aberta para continuar',
        titleNewTab: 'Conectado em outra aba',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Utilize a ligação de verificação enviada para o seu e-mail',
      resendButton: 'Não recebeu a ligação? Reenviar',
      subtitle: 'para continuar para {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'Código de redefinição de palavra-passe',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'to reset your password',
      subtitle_email: 'First, enter the code sent to your email ID',
      subtitle_phone: 'First, enter the code sent to your phone',
      title: 'Reset password',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Repor a palavra-passe',
      label__alternativeMethods: 'Ou, faça login com outro método.',
      title: 'Esqueceu-se da palavra-passe?',
    },
    newDeviceVerificationNotice:
      'Está a iniciar sessão a partir de um novo dispositivo. Estamos a solicitar verificação para manter a sua conta segura.',
    noAvailableMethods: {
      message: 'Não foi possível fazer login. Não há nenhum método de autenticação disponível.',
      subtitle: 'Ocorreu um erro',
      title: 'Não foi possível fazer login',
    },
    passkey: {
      subtitle: 'Utilize a sua chave de acesso para autenticação.',
      title: 'Autenticação com Chave de Acesso',
    },
    password: {
      actionLink: 'Utilize outro método',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Insira a sua palavra-passe',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: 'Este password foi comprometido em uma violação de dados. Escolha outro por motivos de segurança.',
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'Código de verificação',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu telemóvel',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificação',
      resendButton: 'Reenviar código',
      subtitle: 'Insira o código enviado para o seu número de telefone',
      title: 'Verifique o seu telemóvel',
    },
    resetPassword: {
      formButtonPrimary: 'Repor Palavra-passe',
      requiredMessage: 'For security reasons, it is required to reset your password.',
      successMessage: 'A sua palavra-passe foi alterada com sucesso. Entrando, por favor aguarde um momento.',
      title: 'Repor Palavra-passe',
    },
    resetPasswordMfa: {
      detailsLabel: 'Precisamos verificar a sua identidade antes de redefinir a palavra-passe.',
    },
    start: {
      actionLink: 'Registre-se',
      actionLink__join_waitlist: 'Juntar-se à lista de espera',
      actionLink__use_email: 'Usar e-mail',
      actionLink__use_email_username: 'Usar e-mail ou nome de utilizador',
      actionLink__use_passkey: 'Usar chave de acesso',
      actionLink__use_phone: 'Usar telemóvel',
      actionLink__use_username: 'Usar nome de utilizador',
      actionText: 'Não possui uma conta?',
      actionText__join_waitlist: 'Ainda não tem uma conta? Junte-se à lista de espera.',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'para continuar em {{applicationName}}',
      subtitleCombined: undefined,
      title: 'Entrar',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: 'Código de verificação',
      subtitle: 'Insira o código de verificação enviado para o seu dispositivo.',
      title: 'Verificação de duas etapas',
    },
    web3Solana: {
      subtitle: 'Selecione uma carteira abaixo para iniciar sessão',
      title: 'Iniciar sessão com Solana',
    },
  },
  signInEnterPasswordTitle: 'Insira a sua palavra-passe',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'Entrar',
      actionText: 'Já possui uma conta?',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Preencha os campos ausentes',
    },
    emailCode: {
      formSubtitle: 'Insira o código enviado para o seu e-mail',
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu o código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Parece que houve um erro com a sua sessão. Tente novamente.',
        title: 'Erro de sessão',
      },
      formSubtitle: 'Utilize o link enviado no seu e-mail',
      formTitle: 'Link de verificação',
      loading: {
        title: 'Entrando...',
      },
      resendButton: 'Reenviar link',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique seu e-mail',
      verified: {
        title: 'Registo realizado com sucesso',
      },
      verifiedSwitchTab: {
        subtitle: 'Volte para a nova aba que foi aberta para continuar',
        subtitleNewTab: 'Volte para a aba anterior para continuar',
        title: 'E-mail verificado com sucesso',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Aceito a Política de Privacidade',
        label__onlyTermsOfService: 'Aceito os Termos de Serviço',
        label__termsOfServiceAndPrivacyPolicy:
          'Aceito os {{ termsOfServiceLink || link("Termos de Serviço") }} e a {{ privacyPolicyLink || link("Política de Privacidade") }}',
      },
      continue: {
        subtitle: 'Ao continuar, você concorda com os termos acima.',
        title: 'Continuar',
      },
    },
    phoneCode: {
      formSubtitle: 'Insira o código enviado para o seu telemóvel',
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu o código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu telemóvel',
    },
    restrictedAccess: {
      actionLink: 'Contactar suporte',
      actionText: 'Precisa de ajuda?',
      blockButton__emailSupport: 'Enviar e-mail para suporte',
      blockButton__joinWaitlist: 'Junte-se à lista de espera',
      subtitle: 'O seu acesso está restrito. Para mais informações, entre em contacto connosco.',
      subtitleWaitlist: 'Estamos a aguardar a sua entrada na lista de espera. Agradecemos pela paciência.',
      title: 'Acesso Restrito',
    },
    start: {
      actionLink: 'Entrar',
      actionLink__use_email: 'Usar e-mail',
      actionLink__use_phone: 'Usar telemóvel',
      actionText: 'Já tem uma conta?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'para continuar em {{applicationName}}',
      subtitleCombined: 'para continuar em {{applicationName}}',
      title: 'Criar a sua conta',
      titleCombined: 'Criar a sua conta',
    },
    web3Solana: {
      subtitle: 'Selecione uma carteira abaixo para se registar',
      title: 'Registar-se com Solana',
    },
  },
  socialButtonsBlockButton: 'Continuar com {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: undefined,
  taskChooseOrganization: {
    chooseOrganization: {
      action__createOrganization: undefined,
      action__invitationAccept: undefined,
      action__suggestionsAccept: undefined,
      subtitle: undefined,
      subtitle__createOrganizationDisabled: undefined,
      suggestionsAcceptedLabel: undefined,
      title: undefined,
    },
    createOrganization: {
      formButtonReset: undefined,
      formButtonSubmit: undefined,
      formFieldInputPlaceholder__name: undefined,
      formFieldInputPlaceholder__slug: undefined,
      formFieldLabel__name: undefined,
      formFieldLabel__slug: undefined,
      subtitle: undefined,
      title: undefined,
    },
    organizationCreationDisabled: {
      title: 'Deve pertencer a uma organização',
      subtitle: 'Contacte o administrador da sua organização para obter um convite.',
    },
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    alerts: {
      organizationAlreadyExists:
        'Já existe uma organização para o nome da empresa detetado ({{organizationName}}) e {{organizationDomain}}. Adira por convite.',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  unstable__errors: {
    already_a_member_in_organization: 'Já é membro nesta organização.',
    avatar_file_size_exceeded:
      'O tamanho do ficheiro excede o limite máximo de 10 MB. Por favor, escolha um ficheiro mais pequeno.',
    avatar_file_type_invalid: 'Tipo de ficheiro não suportado. Por favor, carregue uma imagem JPG, PNG, GIF ou WEBP.',
    captcha_invalid:
      'Não foi possível inscrever-se devido a falhas nas validações de segurança. Por favor, atualize a página para tentar novamente ou entre em contato com o suporte para obter mais ajuda.',
    captcha_unavailable:
      'Inscrição mal-sucedida devido a falha na validação de bot. Por favor, atualize a página para tentar novamente ou entre em contato com o suporte para obter mais ajuda.',
    form_code_incorrect: 'Código incorreto.',
    form_email_address_blocked:
      'Serviços de e-mail temporários não são suportados. Por favor, use o seu endereço de e-mail regular para criar uma conta.',
    form_identifier_exists__email_address: 'O endereço de e-mail já está em uso.',
    form_identifier_exists__phone_number: 'O número de telemóvel já está em uso.',
    form_identifier_exists__username: 'O nome de utilizador já está em uso.',
    form_identifier_not_found: 'Não foi possível encontrar uma conta com esses detalhes.',
    form_new_password_matches_current: undefined,
    form_param_format_invalid: 'Formato de parâmetro inválido.',
    form_param_format_invalid__email_address: 'O endereço de e-mail deve ser válido.',
    form_param_format_invalid__phone_number: 'O número de telemóvel deve ser válido.',
    form_param_max_length_exceeded__first_name: 'O primeiro nome não deve exceder 256 caracteres.',
    form_param_max_length_exceeded__last_name: 'O apelido não deve exceder 256 caracteres.',
    form_param_max_length_exceeded__name: 'O nome não deve exceder 256 caracteres.',
    form_param_nil: 'Parâmetro não pode ser nulo.',
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: 'Valor de parâmetro inválido.',
    form_password_incorrect: 'Palavra-passe incorreta.',
    form_password_or_identifier_incorrect:
      'A palavra-passe ou o endereço de e-mail está incorreto. Tente novamente ou use outro método.',
    form_password_length_too_short: 'A palavra-passe é muito curta.',
    form_password_not_strong_enough: 'A sua palavra-passe não é forte o suficiente.',
    form_password_pwned:
      'Esta palavra-passe foi encontrada como parte de uma violação e não pode ser usada, por favor, tente outra palavra-passe.',
    form_password_pwned__sign_in:
      'Esta palavra-passe foi encontrada como parte de uma violação e não pode ser utilizada para login. Por favor, escolha outra.',
    form_password_size_in_bytes_exceeded:
      'A sua palavra-passe excedeu o número máximo de bytes permitidos, por favor, encurte-a ou remova alguns caracteres especiais.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'Falha na validação da palavra-passe.',
    form_username_invalid_character: 'O nome de utilizador contém caracteres inválidos.',
    form_username_invalid_length: 'O nome de utilizador deve ter entre 3 e 50 caracteres.',
    form_username_needs_non_number_char: 'O seu nome de utilizador deve conter pelo menos um caractere não numérico.',
    identification_deletion_failed: 'Você não pode excluir a sua última identificação.',
    not_allowed_access:
      "O endereço de e-mail ou número de telefone não é permitido para registro. Isso pode ser devido ao uso de '+', '=', '#' ou '.' no endereço de e-mail, o uso de um domínio associado a um serviço de e-mail temporário ou uma exclusão explícita.",
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: undefined,
    passkey_not_supported: undefined,
    passkey_pa_not_supported: undefined,
    passkey_registration_cancelled: undefined,
    passkey_retrieval_cancelled: undefined,
    passwordComplexity: {
      maximumLength: 'menos de {{length}} caracteres',
      minimumLength: '{{length}} ou mais caracteres',
      requireLowercase: 'uma letra minúscula',
      requireNumbers: 'um número',
      requireSpecialCharacter: 'um caractere especial',
      requireUppercase: 'uma letra maiúscula',
      sentencePrefix: 'A sua palavra-passe deve conter',
    },
    phone_number_exists: 'Este número de telemóvel já está em uso. Por favor, tente outro.',
    session_exists: 'Já está conectado.',
    web3_missing_identifier: undefined,
    web3_signature_request_rejected: 'Rejeitou o pedido de assinatura. Tente novamente para continuar.',
    web3_solana_signature_generation_failed: 'Ocorreu um erro ao gerar a assinatura. Tente novamente para continuar.',
    zxcvbn: {
      couldBeStronger: 'A sua palavra-passe funciona, mas poderia ser mais forte. Tente adicionar mais caracteres.',
      goodPassword: 'A sua palavra-passe atende a todos os requisitos necessários.',
      notEnough: 'A sua palavra-passe não é forte o suficiente.',
      suggestions: {
        allUppercase: 'Utilize apenas algumas letras maiúsculas, não todas.',
        anotherWord: 'Adicione palavras menos comuns.',
        associatedYears: 'Evite anos associados a você.',
        capitalization: 'Utilize outras letras maiúsculas, além do que primeira.',
        dates: 'Evite datas e anos associados a você.',
        l33t: "Evite substituições previsíveis de letras, como '@' por 'a'.",
        longerKeyboardPattern: 'Use padrões de teclado mais longos e mude a direção da digitação várias vezes.',
        noNeed: 'Você pode criar palavras-passes fortes sem usar símbolos, números ou letras maiúsculas.',
        pwned: 'Se usar esta palavra-passe noutro lugar, deve mudá-la.',
        recentYears: 'Evite anos recentes.',
        repeated: 'Evite palavras e caracteres repetidos.',
        reverseWords: 'Evite utilizar palavras comuns escritas de "trás para frente".',
        sequences: 'Evite sequências comuns de caracteres.',
        useWords: 'Use várias palavras, mas evite frases comuns.',
      },
      warnings: {
        common: 'Esta é uma palavra-passe comumente usada.',
        commonNames: 'Nomes e apelidos comuns são fáceis de adivinhar.',
        dates: 'Datas são fáceis de adivinhar.',
        extendedRepeat: 'Padrões de caracteres repetidos, como "abcabcabc" são fáceis de adivinhar.',
        keyPattern: 'Padrões curtos de teclado são fáceis de adivinhar.',
        namesByThemselves: 'Nomes ou apelidos são fáceis de adivinhar.',
        pwned: 'A sua palavra-passe foi exposta numa violação de dados na Internet.',
        recentYears: 'Anos recentes são fáceis de adivinhar.',
        sequences: 'Sequências comuns de caracteres, como "abc" são fáceis de adivinhar.',
        similarToCommon: 'Esta é semelhante a uma palavra-passe comumente usada.',
        simpleRepeat: 'Caracteres repetidos, como "aaa" são fáceis de adivinhar.',
        straightRow: 'Letras que vêm em sequência no teclado são fáceis de adivinhar.',
        topHundred: 'Esta é uma palavra-passe usada frequentemente.',
        topTen: 'Esta é uma palavra-passe muito usada.',
        userInputs: 'Não deve haver nenhum dado pessoal ou relacionado à página.',
        wordByItself: 'Palavras simples são fáceis de adivinhar.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Adicionar conta',
    action__closeUserMenu: 'Fechar menu do utilizador',
    action__manageAccount: 'Configurar conta',
    action__openUserMenu: 'Abrir menu do utilizador',
    action__signOut: 'Terminar sessão',
    action__signOutAll: 'Terminar sessão de todas as contas',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: 'Copiado!',
      actionLabel__copy: 'Copiar tudo',
      actionLabel__download: 'Download .txt',
      actionLabel__print: 'Imprimir',
      infoText1: 'Códigos de backup serão ativados para esta conta.',
      infoText2:
        'Guarde-os em segurança e não os partilhe. Você pode gerar novos códigos de backup se suspeitar que eles tenham sido comprometidos.',
      subtitle__codelist: 'Guarde-os em segurança e não os partilhe.',
      successMessage:
        'Códigos de backup foram ativados para esta conta. Pode usar um deles para fazer login na sua conta caso perca o acesso ao seu dispositivo de autenticação. Cada código poderá ser utilizado apenas uma vez.',
      successSubtitle:
        'Pode usar um deles para fazer login na sua conta caso perca o acesso ao seu dispositivo de autenticação.',
      title: 'Adicionar código de backup para verificação',
      title__codelist: 'Códigos de backup',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: 'Selecione um provedor para conectar à sua conta.',
      formHint__noAccounts: 'Não há provedores de conta externos disponíveis.',
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Não vai conseguir usar esta conta e, quaisquer recursos dependentes dela deixarão de funcionar.',
        successMessage: '{{connectedAccount}} foi removido da sua conta.',
        title: 'Remover conta conectada',
      },
      socialButtonsBlockButton: 'Conectar conta {{provider|titleize}}',
      successMessage: 'O provedor foi adicionado à sua conta',
      title: 'Conecte uma conta',
    },
    deletePage: {
      actionDescription: 'Escreva Excluir conta abaixo para continuar.',
      confirm: 'Excluir conta',
      messageLine1:
        'Tem a certeza de que pretende eliminar a sua conta? Alguns dados associados poderão ser retidos. Para solicitar a eliminação completa de dados, contacte o suporte.',
      messageLine2: 'Esta ação é permanente e irreversível.',
      title: 'Excluir conta',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Um e-mail contendo um código de verificação será enviado para este endereço de e-mail.',
        formSubtitle: 'Insira o código de verificação enviado para {{identifier}}',
        formTitle: 'Código de verificação',
        resendButton: 'Não recebeu um código? Reenviar',
        successMessage: 'O e-mail {{identifier}} foi adicionado à sua conta.',
      },
      emailLink: {
        formHint: 'Um e-mail contendo um link de verificação será enviado para este endereço de e-mail.',
        formSubtitle: 'Clique no link de verificação enviado para {{identifier}}',
        formTitle: 'Link de verificação',
        resendButton: 'Não recebeu um código? Reenviar',
        successMessage: 'O e-mail {{identifier}} foi adicionado à sua conta.',
      },
      enterpriseSSOLink: {
        formButton: 'Clique para autenticar',
        formSubtitle: 'Complete a autenticação com {{identifier}}',
      },
      formHint: 'Você precisará verificar este endereço de email antes de poder adicioná-lo à sua conta.',
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Não vai conseguir fazer login novamente com este endereço de e-mail.',
        successMessage: '{{emailAddress}} foi removido da sua conta.',
        title: 'Remover e-mail',
      },
      title: 'Adicionar e-mail',
      verifyTitle: 'Verify email address',
    },
    formButtonPrimary__add: 'Add',
    formButtonPrimary__continue: 'Continuar',
    formButtonPrimary__finish: 'Finalizar',
    formButtonPrimary__remove: 'Remove',
    formButtonPrimary__save: 'Save',
    formButtonReset: 'Cancelar',
    mfaPage: {
      formHint: 'Selecione um método para adicionar.',
      title: 'Adicione verificação de duas etapas',
    },
    mfaPhoneCodePage: {
      backButton: 'Use existing number',
      primaryButton__addPhoneNumber: 'Adicione um número de telemóvel',
      removeResource: {
        messageLine1: '{{identifier}} não receberá mais códigos de verificação ao realizar o login.',
        messageLine2: 'A sua conta pode ficar menos segura. Tem certeza que deseja continuar?',
        successMessage: 'Código SMS de verificação de duas etapas foi removido para {{mfaPhoneCode}}',
        title: 'Remover verificação de duas etapas',
      },
      subtitle__availablePhoneNumbers:
        'Selecione um número de telemóvel para registrar a verificação de duas etapas por código SMS.',
      subtitle__unavailablePhoneNumbers:
        'Não há números de telemóvel disponíveis para registrar a verificação de duas etapas por código SMS.',
      successMessage1:
        'When signing in, you will need to enter a verification code sent to this phone number as an additional step.',
      successMessage2:
        'Save these backup codes and store them somewhere safe. If you lose access to your authentication device, you can use backup codes to sign in.',
      successTitle: 'SMS code verification enabled',
      title: 'Adicionar verificação por SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Ler código QR em vez disso',
        buttonUnableToScan__nonPrimary: 'Não pode ler o código QR?',
        infoText__ableToScan:
          'Configure um novo método de login no seu autenticador e leia o seguinte código QR para vinculá-lo à sua conta.',
        infoText__unableToScan:
          'Configure um novo método de login no seu autenticador e insira a chave informada abaixo.',
        inputLabel__unableToScan1:
          "Certifique-se de que o 'One-time passwords' está ativo, de seguida, conclua a conexão da sua conta.",
        inputLabel__unableToScan2:
          'Alternativamente, se o seu autenticador suportar URIs TOTP, também pode copiar a URI completa.',
      },
      removeResource: {
        messageLine1: 'Os códigos de verificação deste autenticador não serão mais necessários ao fazer login.',
        messageLine2: 'A sua conta pode ficar menos segura. Tem certeza que deseja continuar?',
        successMessage: 'A verificação de duas etapas via autenticador foi removida.',
        title: 'Remover verificação de duas etapas',
      },
      successMessage:
        'A verificação de duas etapas está agora ativa. Ao fazer login, precisará de inserir um código de verificação deste autenticador como uma etapa adicional.',
      title: 'Adicionar um autenticador',
      verifySubtitle: 'Insira o código de verificação gerado pelo seu autenticador',
      verifyTitle: 'Código de verificação',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Profile',
      apiKeys: undefined,
      billing: undefined,
      description: 'Manage your account info.',
      security: 'Security',
      title: 'Account',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: 'Tem a certeza de que deseja remover este recurso?',
        title: 'Remover Recurso',
      },
      subtitle__rename: 'Altere o nome do recurso, se necessário.',
      title__rename: 'Renomear Recurso',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'It is recommended to sign out of all other devices which may have used your old password.',
      readonly: 'A sua palavra-passe não pode ser editada porque só pode fazer login por meio da conexão da empresa.',
      successMessage__set: 'A sua palavra-passe foi guardada.',
      successMessage__signOutOfOtherSessions: 'Todos os outros dispositivos foram desconectados.',
      successMessage__update: 'A sua palavra-passe foi atualizada.',
      title__set: 'Defina a palavra-passe',
      title__update: 'Trocar palavra-passe',
    },
    phoneNumberPage: {
      infoText: 'Um SMS contendo um link de verificação será enviado para este telemóvel.',
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Não vai conseguir fazer login novamente com este número de telemóvel.',
        successMessage: '{{phoneNumber}} foi removido da sua conta.',
        title: 'Remover telemóvel',
      },
      successMessage: '{{identifier}} foi adicionado à sua conta.',
      title: 'Adicionar telemóvel',
      verifySubtitle: 'Enter the verification code sent to {{identifier}}',
      verifyTitle: 'Verify phone number',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'Carregue uma imagem JPG, PNG, GIF ou WEBP menor que 10MB',
      imageFormDestructiveActionSubtitle: 'Remover imagem',
      imageFormSubtitle: 'Carregar imagem',
      imageFormTitle: 'Imagem de perfil',
      readonly: 'As informações do perfil foram fornecidas pela conexão corporativa e não podem ser editadas.',
      successMessage: 'O perfil foi atualizado.',
      title: 'Atualizar perfil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Terminar sessão',
        title: 'Dispositivos ativos',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Tentar novamente',
        actionLabel__reauthorize: 'Reautorizar agora',
        destructiveActionTitle: 'Remover',
        primaryButton: 'Conectar conta',
        subtitle__disconnected: 'A conta foi desconectada. Clique abaixo para conectar novamente.',
        subtitle__reauthorize:
          'The required scopes have been updated, and you may be experiencing limited functionality. Please re-authorize this application to avoid any issues',
        title: 'Contas conectadas',
      },
      dangerSection: {
        deleteAccountButton: 'Excluir Conta',
        title: 'Perigo',
      },
      emailAddressesSection: {
        destructiveAction: 'Remover e-mail',
        detailsAction__nonPrimary: 'Definir como principal',
        detailsAction__primary: 'Completar verificação',
        detailsAction__unverified: 'Completar verificação',
        primaryButton: 'Adicionar um e-mail',
        title: 'Endereços de e-mail',
      },
      enterpriseAccountsSection: {
        title: 'Contas corporativas',
      },
      headerTitle__account: 'Conta',
      headerTitle__security: 'Segurança',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Gerar novos códigos',
          headerTitle: 'Códigos de backup',
          subtitle__regenerate:
            'Obter um novo conjunto de códigos de backup seguros. Os códigos de backup anteriores serão excluídos e não poderão ser utilizados novamente.',
          title__regenerate: 'Gerar novos códigos de backup',
        },
        phoneCode: {
          actionLabel__setDefault: 'Definir como principal',
          destructiveActionLabel: 'Remover telemóvel',
        },
        primaryButton: 'Adicione verificação',
        title: 'Verificação de duas etapas',
        totp: {
          destructiveActionTitle: 'Remover',
          headerTitle: 'Autenticador',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Remover chave de acesso',
        menuAction__rename: 'Renomear chave de acesso',
        primaryButton: undefined,
        title: 'Chaves de Acesso',
      },
      passwordSection: {
        primaryButton__setPassword: undefined,
        primaryButton__updatePassword: undefined,
        title: undefined,
      },
      phoneNumbersSection: {
        destructiveAction: 'Remover telemóvel',
        detailsAction__nonPrimary: 'Definir como principal',
        detailsAction__primary: 'Completar verificação',
        detailsAction__unverified: 'Completar verificação',
        primaryButton: 'Adicione um telemóvel',
        title: 'Números de telemóvel',
      },
      profileSection: {
        primaryButton: 'Salvar alterações',
        title: 'Perfil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Definir nome de utilizador',
        primaryButton__updateUsername: 'Trocar nome de utilizador',
        title: 'Nome de utilizador',
      },
      web3WalletsSection: {
        destructiveAction: 'Remover carteira',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'Carteiras Web3',
        title: 'Carteiras Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Selecione uma carteira Solana para ligar à sua conta.',
          title: 'Adicionar uma carteira Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'O nome de utilizador foi atualizado.',
      title__set: 'Atualizar nome de utilizador',
      title__update: 'Atualizar nome de utilizador',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Não vai conseguir usar esta carteira Web3 para entrar na sua conta.',
        successMessage: '{{Web3Wallet}} foi removido da sua conta.',
        title: 'Remover carteira Web3',
      },
      subtitle__availableWallets: 'Selecione uma carteira Web3 para conectar à sua conta.',
      subtitle__unavailableWallets: 'Não há carteiras Web3 disponíveis.',
      successMessage: 'A carteira foi adicionada à sua conta.',
      title: 'Adicionar carteira Web3',
      web3WalletButtonsBlockButton: 'Conectar carteira Web3',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Junte-se à lista de espera',
      actionText: 'Ainda não tem uma conta? Junte-se à lista de espera.',
      formButton: 'Submeter',
      subtitle: 'Deixe os seus dados para se juntar à lista de espera.',
      title: 'Juntar-se à lista de espera',
    },
    success: {
      message: 'Obrigado por se inscrever! Você será notificado assim que tiver acesso.',
      subtitle: 'Aguarde enquanto processamos o seu pedido.',
      title: 'Inscrição bem-sucedida na lista de espera',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Conectar com {{walletName}}',
    continue: 'Continuar com {{walletName}}',
    noneAvailable:
      'Não foram detetadas carteiras Solana Web3. Instale uma {{ solanaWalletsLink || link("wallet extension") }} com suporte Web3.',
  },
} as const;
